import { useEffect, useState } from "react";
import { getTeacherDisplayName } from "../helpers/schoolClassHelpers";
import { getClassByToken, getClassTeacherInfo } from "../networking/schoolClasses";

export interface ClassDisplayInfo {
    className: string;
    teacherDisplayName: string;
}

/**
 * Class name and teacher for sublines. Fails soft: a failed lookup leaves that field empty instead of
 * throwing. `null` until both lookups settled, so a caller can hold a line back instead of showing a
 * fallback and swapping it a beat later. An empty id skips the fetch.
 */
export const useClassDisplayInfo = (schoolClassId: string): ClassDisplayInfo | null => {
    const [info, setInfo] = useState<ClassDisplayInfo | null>(null);

    useEffect(() => {
        if (!schoolClassId) return;
        let cancelled = false;
        setInfo(null);
        (async () => {
            const [classResult, teacherResult] = await Promise.allSettled([
                getClassByToken(schoolClassId),
                getClassTeacherInfo(schoolClassId),
            ]);
            if (cancelled) return;
            setInfo({
                className: classResult.status === "fulfilled" ? classResult.value.name : "",
                teacherDisplayName: getTeacherDisplayName(
                    teacherResult.status === "fulfilled" ? teacherResult.value : null
                ),
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolClassId]);

    return info;
};
